/**
 * Story list/detail items flatten locale blocks at top level (e.g. en, fr) per `/v1/story` API spec.
 */

const LOCALE_KEY_HINTS = /^[a-z]{2}(-[A-Za-z]{2})?$/;

function isStoryLocaleBlock(val) {
    return Boolean(val && typeof val === "object" && !Array.isArray(val) && typeof val.title === "string");
}

/**
 * English-only title from `en` locale block.
 * @param {Record<string, unknown>} story
 * @returns {string}
 */
export function getStoryEnglishTitle(story) {
    if (!story || typeof story !== "object") return "Untitled";
    const en = story.en;
    if (isStoryLocaleBlock(en) && en.title.trim()) return en.title.trim();
    return typeof story.id === "string" && story.id.length >= 8 ? `${story.id.slice(0, 8)}…` : "Untitled";
}

/** English story body from `en.text` (legacy `english.text`). */
export function getStoryEnglishText(story) {
    if (!story || typeof story !== "object") return "";
    const blocks = [story.en, story.english];
    for (const block of blocks) {
        if (block && typeof block === "object" && typeof block.text === "string" && block.text.trim()) {
            return block.text.trim();
        }
        if (typeof block === "string" && block.trim()) return block.trim();
    }
    if (typeof story.text === "string" && story.text.trim()) return story.text.trim();
    return "";
}

/**
 * `publishedAt` from embedded `gridTpl` or `crossTpl` / `crosswordTpl`.
 * @param {Record<string, unknown>} story
 * @returns {number|null}
 */
export function getStoryTplPublishedAt(story) {
    if (!story || typeof story !== "object") return null;

    const gridTpl = story.gridTpl && typeof story.gridTpl === "object" ? story.gridTpl : null;
    const crossTpl =
        (story.crossTpl && typeof story.crossTpl === "object" && story.crossTpl) ||
        (story.crosswordTpl && typeof story.crosswordTpl === "object" && story.crosswordTpl) ||
        null;
    const tpl = gridTpl || crossTpl;
    if (!tpl) return null;

    const at = tpl.publishedAt;
    if (at == null || at === 0) return null;
    return typeof at === "number" ? at : null;
}

/**
 * @param {Record<string, unknown>} story
 * @returns {number|null}
 */
export function getStoryFacebookPublishedAt(story) {
    if (!story || typeof story !== "object") return null;
    const at = story.facebookPublishedAt ?? story.facebook_published_at;
    if (at == null || at === 0) return null;
    return typeof at === "number" ? at : null;
}

/** @param {Record<string, unknown>} story */
export function getStoryInstagramPublishedAt(story) {
    if (!story || typeof story !== "object") return null;
    const at = story.instagramPublishedAt ?? story.instagram_published_at;
    if (at == null || at === 0) return null;
    return typeof at === "number" ? at : null;
}

/** @param {Record<string, unknown>} story */
export function getStoryHighPriorityPublish(story) {
    if (!story || typeof story !== "object") return false;
    return story.highPriorityPublish === true || story.high_priority_publish === true;
}

/**
 * First non-empty localized title found (prefers english, then en).
 * @param {Record<string, unknown>} story
 * @returns {string}
 */
export function getStoryDisplayTitle(story) {
    if (!story || typeof story !== "object") return "Untitled";
    const order = ["english", "en", "fr", "french", "de", "es"];
    for (const k of order) {
        const block = story[k];
        if (isStoryLocaleBlock(block) && block.title.trim()) return block.title.trim();
    }
    for (const [k, v] of Object.entries(story)) {
        if (!LOCALE_KEY_HINTS.test(k)) continue;
        if (isStoryLocaleBlock(v) && v.title.trim()) return v.title.trim();
    }
    return typeof story.id === "string" && story.id.length >= 8 ? `${story.id.slice(0, 8)}…` : "Untitled";
}

/**
 * Thumbnail for admin list cards — prefer `imageIcon400Url`, then `imageIcon200Url`.
 * @param {Record<string, unknown>} story
 * @returns {string|null}
 */
export function getStoryCardImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const icon400 = story.imageIcon400Url ?? story.image_icon_400_url;
    if (typeof icon400 === "string" && icon400.trim()) return icon400.trim();
    const icon200 = story.imageIcon200Url ?? story.image_icon_200_url;
    if (typeof icon200 === "string" && icon200.trim()) return icon200.trim();
    return null;
}

/** Full illustration for the list popup — prefer `imageUrl`, then preview, then card thumbnail. */
export function getStoryFullImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const main = story.imageUrl ?? story.image_url;
    if (typeof main === "string" && main.trim()) return main.trim();
    const preview = story.imagePreviewUrl ?? story.image_preview_url;
    if (typeof preview === "string" && preview.trim()) return preview.trim();
    return getStoryCardImageUrl(story);
}

function trimStr(v) {
    if (typeof v !== "string") return null;
    const t = v.trim();
    return t || null;
}

/** Tags from grid/crossword tpl: `tags[]` or `topic.tags`. */
function templateTagsList(tpl) {
    if (!tpl || typeof tpl !== "object") return [];
    const raw = tpl.tags ?? tpl.topic?.tags;
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const item of raw) {
        const s =
            typeof item === "string"
                ? item.trim()
                : typeof item?.name === "string"
                  ? item.name.trim()
                  : typeof item?.tag === "string"
                    ? item.tag.trim()
                    : "";
        if (s) out.push(s);
    }
    return out;
}

/**
 * Embedded grid/crossword template on GET `/v1/story/{id}` (heavy objects — only scalar summary fields).
 * @param {Record<string, unknown>} story
 * @returns {{ puzzleKind: string, id: string|null, name: string|null, title: string|null, state: string|null, type: string|null, difficultyLevel: string|null, language: string|null, tags: string[] } | null}
 */
export function getStoryTemplateSummary(story) {
    if (!story || typeof story !== "object") return null;

    const gridTpl = story.gridTpl && typeof story.gridTpl === "object" ? story.gridTpl : null;
    const crosswordTpl =
        (story.crosswordTpl && typeof story.crosswordTpl === "object" && story.crosswordTpl) ||
        (story.crossTpl && typeof story.crossTpl === "object" && story.crossTpl) ||
        null;
    const crossFromCrossword =
        story.crossword &&
        typeof story.crossword === "object" &&
        story.crossword.crossTpl &&
        typeof story.crossword.crossTpl === "object"
            ? story.crossword.crossTpl
            : null;

    const tpl = gridTpl || crosswordTpl || crossFromCrossword;
    if (!tpl) return null;

    const puzzleKind = gridTpl ? "Grid" : "Crossword";

    return {
        puzzleKind,
        id: typeof tpl.id === "string" ? tpl.id : null,
        name: trimStr(tpl.name),
        title: trimStr(tpl.title),
        state: trimStr(tpl.state),
        type: trimStr(tpl.type),
        difficultyLevel: trimStr(tpl.difficultyLevel),
        language: trimStr(tpl.language) || trimStr(story.language),
        tags: templateTagsList(tpl),
    };
}
